import React, { useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { DateTime } from 'luxon';
import {
  faClockRotateLeft,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import Alert from 'app/src/elements/Alert';
import IconWithTitle from 'app/src/elements/IconWithTitle';
import mobileTable from 'app/src/elements/Table/MobileTable';
import RelayTable from 'app/src/elements/Table/RelayTable';
import useRest from 'app/src/hooks/useRest';
import useScreenSize from 'app/src/hooks/useScreenSize';
import useToggle from 'app/src/hooks/useToggle';
import { Title, Text, Setting, SettingLabel } from 'app/src/styles';
import { dateShort, datetimeShort } from 'app/src/utils/formatting';
import { Link, useOutletContext } from 'app/src/utils/routing';

import Filter from './Filter';
import MobileRow from './MobileRow';
import { DESTROY_EVENT, EVENTS, EVENT_SELECTS } from './queries';

export const nodes = collection => (
  collection ? collection.edges.map(edge => edge.node) : []
);

const TimeWithWarning = ({ timeISO, eventAtISO, label }) => {
  const eventAt = DateTime.fromISO(eventAtISO);
  const time = DateTime.fromISO(timeISO);

  return (
    <View style={{ flexDirection: 'row', gap: 6 }}>
      <Text>{datetimeShort(timeISO)}</Text>

      {eventAt > time && (
        <IconWithTitle
          icon={faTriangleExclamation}
          title={`Event was ${label} prior to event time.`}
        />
      )}

      {eventAt < time.minus({ days: 1 }) && (
        <IconWithTitle
          icon={faClockRotateLeft}
          title={`Event was ${label} more than 1 day after event time.`}
        />
      )}
    </View>
  );
}

const baseColumns = [
  {
    key: 'eventAt',
    label: 'Time',
    sort: 'desc',
    content: ({ entry }) => {
      switch (entry.timeType) {
        case 'date_only':
          return <Text>{dateShort(entry.eventAt.split('T')[0])}</Text>;
        case 'created_at':
          return <Text>{datetimeShort(entry.eventAt)} (Time Entered)</Text>;
        case 'estimated':
          return <Text>{datetimeShort(entry.eventAt)} (Est.)</Text>;
        default:
          return <Text>{datetimeShort(entry.eventAt)}</Text>;
      }
    }
  },
  {
    key: 'client',
    label: 'Client',
    value: ({ value }) => value.name,
    content: ({ entry, value }) => (
      value && (
        <Link to={`/clients/${entry.client.id}`}>
          <Text>{value}</Text>
        </Link>
      )
    ),
  },
  {
    key: 'eventType',
    label: 'Type',
    value: ({ value }) => value.name,
    content: ({ entry, value }) => (
      value && (
        <Link to={`/event-types/${entry.eventType.id}`}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={{ marginRight: 10 }}>{value}</Text>
            {entry.things.map(thing => (
              <Text key={thing.id}>
                {thing.emoji || thing.name.substr(0, 8)}{' '}
              </Text>
            ))}
          </View>
        </Link>
      )
    ),
  },
  {
    key: 'amount',
    label: 'Amount',
  },
  {
    key: 'duration',
    label: 'Duration',
    value: ({ entry }) => entry.durationDescription,
  },
  {
    key: 'createdAt',
    label: 'Entered',
    hidden: true,
    content: ({ value, entry }) => (
      <TimeWithWarning
        timeISO={value}
        eventAtISO={entry.eventAt}
        label="entered"
      />
    )
  },
  {
    key: 'updatedAt',
    label: 'Updated',
    hidden: true,
    content: ({ value, entry }) => (
      value === entry.createdAt ? '' : (
        <TimeWithWarning
          timeISO={value}
          eventAtISO={entry.eventAt}
          label="updated"
        />
      )
    )
  },
  {
    key: 'actions',
    sort: 'prevent',
    content: () => null,
  },
];

const Events = () => {
  const client = useOutletContext()?.client;
  const { download } = useRest();
  const { isScreenSize } = useScreenSize();
  const [showTimestamps, toggleShowTimestamps] = useToggle();
  const [filters, setFilters] = useState({ clientIds: [client?.id] });
  const [refetch, setRefetch] = useState();

  const { data: selects } = useQuery(EVENT_SELECTS);

  const [destroyEvent] = useMutation(DESTROY_EVENT, {
    onCompleted: () => refetch()
  });

  const handleClear = event => {
    Alert.alert(
      'Really clear event?',
      `${event.eventType.name} - ${event.client.name}`,
      [
        { text: 'No', onPress: () => false, style: 'cancel' },
        {
          text: 'Yes',
          onPress: () => {
            destroyEvent({ variables: { input: { id: event.id } } })
          },
        },
      ]
    );
  }

  const columns = [...baseColumns];
  columns[1] = { ...columns[1], hidden: !!client };
  columns[5] = { ...columns[5], hidden: !showTimestamps };
  columns[6] = { ...columns[6], hidden: !showTimestamps };

  const eventsPath = client ? `/clients/${client.id}/events` : '/events';

  columns[columns.length - 1].content = ({ entry }) => (
    <View style={{ flexDirection: 'row', gap: 12 }}>
      <Link to={`${eventsPath}/${entry.id}`}>
        <Text>View/Edit</Text>
      </Link>

      <Pressable onPress={() => handleClear(entry)}>
        <Text>Clear</Text>
      </Pressable>
    </View>
  );

  const newPath = client ? `/clients/${client.id}/events/new` : `/events/new`;

  const updateFilters = (key, values) => {
    setFilters({ ...filters, [key]: values.length > 0 ? values : null });
  }

  return (
    <View>
      <Title>Events{client && ` for ${client.name}`}</Title>

      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Link to={newPath}>
          <Text>Add Event</Text>
        </Link>

        <Pressable
          onPress={() => download({ path: 'reports/events', extension: 'csv' })}
        >
          <Text>Download CSV</Text>
        </Pressable>
      </View>

      <Setting style={{ marginTop: 12 }}>
        <SettingLabel>Show entered and updated timestamps</SettingLabel>
        <Switch value={showTimestamps} onValueChange={toggleShowTimestamps} />
      </Setting>

      <Filter
        label="Filter By Client"
        filterKey="clientIds"
        options={nodes(selects?.clients)}
        updateFilters={updateFilters}
      />

      <Filter
        label="Filter By Event Type"
        filterKey="eventTypeIds"
        options={nodes(selects?.eventTypes)}
        updateFilters={updateFilters}
      />

      <RelayTable
        query={EVENTS}
        queryName="events"
        queryVariables={{ options: { eventsFilters: filters } }}
        columns={columns}
        setRefetch={setRefetch}
        component={isScreenSize('mobile') ? mobileTable : null}
        componentProps={{ rowComponent: MobileRow }}
      />
    </View>
  );
};

export default Events;
